import {
    useMemo,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IKbdProps from "./type";
import type {
    KbdResolvedKey
} from "./type";
import stylesheet, {
    getKbdColors,
    getKbdSize,
    useStyles
} from "./stylesheet";
import {
    getKbdSeparatorVisibility,
    getKbdAccessibilityLabel,
    useKbdTouchOnly,
    getKbdPlatform,
    resolveKbdKey,
    parseKbdKeys
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import Text from "../text";

const Kbd: FC<IKbdProps> = ({
    displayBehaviour = "auto",
    platform: platformProp,
    variant = "outline",
    isDisabled = false,
    separator = "auto",
    isInField = false,
    isInverse = false,
    customLocalize,
    size = "small",
    customTheme,
    style,
    keys,
    ...props
}) => {
    const {
        shapeConfigs,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isTouchOnly = useKbdTouchOnly();

    const platform = useMemo(() => platformProp ?? getKbdPlatform(), [
        platformProp
    ]);

    const resolvedKeys = parseKbdKeys({
        keys
    }).map((key, index) => resolveKbdKey({
        localize,
        platform,
        index,
        key
    }));

    const isShowSeparator = getKbdSeparatorVisibility({
        separator,
        platform
    });

    const currentSize = getKbdSize({
        typography,
        isInField,
        radiuses,
        spaces,
        size
    });

    const currentColors = getKbdColors({
        isDisabled,
        isInverse,
        variant,
        colors
    });

    const {
        hiddenName: hiddenNameDynamicStyle,
        container: containerDynamicStyle,
        key: keyDynamicStyle
    } = useStyles({
        currentColors,
        shapeConfigs,
        currentSize,
        borders,
        spaces
    });

    if((displayBehaviour === "auto" && isTouchOnly) || !resolvedKeys.length) {
        return null;
    }

    const accessibilityProps = Platform.OS === "web" ? {} : {
        accessibilityLabel: getKbdAccessibilityLabel({
            keys: resolvedKeys,
            isShowSeparator
        }),
        accessible: true
    };

    const renderSeparator = (key: KbdResolvedKey) => {
        return <Text
            customColor={currentColors.separatorColor}
            variant={currentSize.labelVariant}
            key={`${key.id}-separator`}
            customTheme={customTheme}
            style={stylesheet.label}
        >
            +
        </Text>;
    };

    const renderKey = (key: KbdResolvedKey, index: number) => {
        return [
            index > 0 && isShowSeparator ? renderSeparator(key) : null,
            <View
                style={[
                    stylesheet.key,
                    keyDynamicStyle
                ]}
                key={key.id}
            >
                <Text
                    customColor={currentColors.labelColor}
                    variant={currentSize.labelVariant}
                    customTheme={customTheme}
                    aria-hidden={!!key.name}
                    style={stylesheet.label}
                >
                    {key.label}
                </Text>
                {
                    key.name ?
                        <Text
                            variant={currentSize.labelVariant}
                            style={[
                                stylesheet.hiddenName,
                                hiddenNameDynamicStyle
                            ]}
                            customTheme={customTheme}
                        >
                            {key.name}
                        </Text>
                    :
                        null
                }
            </View>
        ];
    };

    return <View
        {...accessibilityProps}
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {resolvedKeys.map(renderKey)}
    </View>;
};
export default Kbd;
